import { useEffect, useState } from 'react';
import {
  getOrdersInWarehouse,
  registerOrderParcelInWarehouse,
} from 'fetch/fetchWarehouseOrders';
import { ReqEnum } from '../const/requestStatus';
import {
  ParcelWithLocation,
  WarehouseOrder,
} from '../pages/WarehouseSystem/WarehouseSystem';
import { vibrate } from '../utils/safeVibrate';
import { AudioPlay } from '../utils/AudioPlay';
import Swal from 'sweetalert2';
import { OrderStatus } from '../types/orderTypes';

export function useFetchWarehouseOrders() {
  const [fetchStatus, setFetchStatus] = useState<string>(ReqEnum.INIT);
  const [orders, setOrders] = useState<WarehouseOrder[]>([]);

  useEffect(() => {
    setFetchStatus(ReqEnum.PENDING);
    const fetchWarehouseOrders = async () => {
      const {
        orders,
        status,
      }: {
        orders: WarehouseOrder[];
        status: string;
      } = await getOrdersInWarehouse();

      setOrders(orders);
      setFetchStatus(status);
    };
    fetchWarehouseOrders();
  }, []);

  return [orders, fetchStatus];
}

function removeOneDuplicate(
  parcelWithLocationList: ParcelWithLocation[],
  parcelWithLocation: ParcelWithLocation
) {
  const indexOfFirstParcelId = parcelWithLocationList.findIndex(
    element =>
      element.parcelId === parcelWithLocation.parcelId &&
      element.warehouseLocation === parcelWithLocation.warehouseLocation
  );

  if (indexOfFirstParcelId === -1) {
    Swal.fire({
      position: 'center',
      icon: 'warning',
      title:
        'The parcel is not registered at this warehouse and can not be removed',
      showConfirmButton: false,
      timer: 2500,
    });
    return parcelWithLocationList;
  }
  parcelWithLocationList[indexOfFirstParcelId] = null;
  const NewParcelList =
    parcelWithLocationList.length > 1
      ? parcelWithLocationList.filter(id => id !== null)
      : [];
  return NewParcelList;
}

export function useFetchWarehouseOrdersArray() {
  const [fetchStatus, setFetchStatus] = useState<string>(ReqEnum.INIT);
  const [orders, setOrders] = useState<WarehouseOrder[]>([]);
  const [scannerIsOpen, setScannerIsOpen] = useState<boolean>(false);
  const [flash, setFlash] = useState<boolean>(false);

  useEffect(() => {
    setFetchStatus(ReqEnum.PENDING);
    const fetchWarehouseOrders = async () => {
      const {
        orders,
        status,
      }: {
        orders: WarehouseOrder[];
        status: string;
      } = await getOrdersInWarehouse();

      setOrders(orders);
      setFetchStatus(status);
    };
    fetchWarehouseOrders();
  }, []);

  async function deleteOrder(orderId: string) {
    const existingOrder: WarehouseOrder[] = orders.filter(
      (order: WarehouseOrder) => order.orderId === orderId
    );
    const updatedOrder = {
      orderId: orderId,
      orderStatus: existingOrder[0].orderStatus,
      createdAt: existingOrder[0].createdAt,
      deliveryDay: existingOrder[0].deliveryDay,
      hasDeviations: existingOrder[0].hasDeviations,
      parcelIds: [],
      updatedAt: new Date(),
    };

    const response = await registerOrderParcelInWarehouse(orderId, []);
    if (response.ok) {
      setOrders(
        orders.map((order: WarehouseOrder) =>
          order.orderId === orderId ? updatedOrder : order
        )
      );
    }
  }

  async function addScannedOrder(
    orderId: string,
    parcelId: string,
    warehouseLocation: string
  ) {
    const parcelWithLocation: ParcelWithLocation = {
      parcelId,
      warehouseLocation,
    };

    const newOrder: WarehouseOrder = {
      orderId: orderId,
      orderStatus: OrderStatus.Ready,
      createdAt: new Date(),
      deliveryDay: '',
      hasDeviations: false,
      parcelIds: [parcelWithLocation],
      updatedAt: new Date(),
    };

    // add city string someewhere

    const response = await registerOrderParcelInWarehouse(orderId, [
      parcelWithLocation,
    ]);
    if (response.ok) {
      setScannerIsOpen(true);
      setOrders([...orders, newOrder]);
    }
  }

  const successScan = () => {
    vibrate(300);
    AudioPlay('sucsessScannOrderId');
    setFlash(true);
    setTimeout(() => {
      setFlash(false);
    }, 1000);
  };

  async function deleteScannedOrder(
    orderId: string,
    parcelId: string,
    warehouseLocation: string
  ) {
    console.log('warehouseLocation', warehouseLocation);
    const parcelWithLocation: ParcelWithLocation = {
      parcelId,
      warehouseLocation,
    };

    const existingOrder: WarehouseOrder[] = orders.filter(
      (order: WarehouseOrder) => order.orderId === orderId
    );
    const existingOrderParcelIds: ParcelWithLocation[] = orders.filter(
      (order: WarehouseOrder) => order.orderId === orderId
    )[0].parcelIds;

    console.log('existingOrderParcelIds', existingOrderParcelIds);
    console.log('parcelWithLocation', parcelWithLocation);

    const UpdatedExistingOrderParcelIds: ParcelWithLocation[] =
      removeOneDuplicate(existingOrderParcelIds, parcelWithLocation);

    console.log('UpdatedExistingOrderParcelIds', UpdatedExistingOrderParcelIds);

    const updatedOrder = {
      orderId: orderId,
      orderStatus: existingOrder[0].orderStatus,
      createdAt: existingOrder[0].createdAt,
      deliveryDay: existingOrder[0].deliveryDay,
      hasDeviations: existingOrder[0].hasDeviations,
      parcelIds: [...UpdatedExistingOrderParcelIds],
      updatedAt: new Date(),
    };

    const response: Response = await registerOrderParcelInWarehouse(
      orderId,
      UpdatedExistingOrderParcelIds
    );
    if (response.ok) {
      successScan();
      setScannerIsOpen(true);
      setOrders(
        orders.map((order: WarehouseOrder) =>
          order.orderId === orderId ? updatedOrder : order
        )
      );
    }
  }

  async function updateScannedOrder(
    orderId: string,
    parcelId: string,
    warehouseLocation: string
  ) {
    const parcelWithLocation: ParcelWithLocation = {
      parcelId,
      warehouseLocation,
    };

    const existingOrder: WarehouseOrder[] = orders.filter(
      (order: WarehouseOrder) => order.orderId === orderId
    );
    const existingOrderParcelIds: ParcelWithLocation[] = orders.filter(
      (order: WarehouseOrder) => order.orderId === orderId
    )[0].parcelIds;

    const updatedOrder: WarehouseOrder = existingOrderParcelIds.length
      ? {
          orderId: orderId,
          orderStatus: existingOrder[0].orderStatus,
          createdAt: existingOrder[0].createdAt,
          deliveryDay: existingOrder[0].deliveryDay,
          hasDeviations: existingOrder[0].hasDeviations,
          parcelIds: [...existingOrderParcelIds, parcelWithLocation],
          updatedAt: new Date(),
        }
      : {
          orderId: orderId,
          orderStatus: existingOrder[0].orderStatus,
          createdAt: existingOrder[0].createdAt,
          deliveryDay: existingOrder[0].deliveryDay,
          hasDeviations: existingOrder[0].hasDeviations,
          parcelIds: [parcelWithLocation],
          updatedAt: new Date(),
        };

    const response: Response = await registerOrderParcelInWarehouse(orderId, [
      ...existingOrderParcelIds,
      parcelWithLocation,
    ]);
    if (response.ok) {
      successScan();
      setScannerIsOpen(true);
      setOrders(
        orders.map((order: WarehouseOrder) =>
          order.orderId === orderId ? updatedOrder : order
        )
      );
    }
  }

  return {
    orders,
    fetchStatus,
    deleteOrder,
    addScannedOrder,
    setScannerIsOpen,
    scannerIsOpen,
    deleteScannedOrder,
    updateScannedOrder,
    flash,
  };
}
